import enGb from 'date-fns/locale/en-GB'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Form, Popup } from 'semantic-ui-react'
import { inputDateTypeFullYearRegex, inputDateTypeRegex, inputDatesTypeRegex } from '../../regex/fieldsValidationRegex'
import { getDatesBetween } from '../../utils/getDatesBetween'
import './Datepicker.css'
registerLocale('en-gb', enGb)

export type PopperPlacement = 'auto-start'
  | 'auto'
  | 'auto-end'
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-end'
  | 'bottom'
  | 'bottom-start'
  | 'left-end'
  | 'left'
  | 'left-start';

interface Props {
  open: boolean
  dateFormat?: string
  customInput?: JSX.Element
  placeholder?: string
  className?: string
  inputBorder?: boolean
  inputColor?: string
  inputBorderSmall?: boolean
  inputMargin?: boolean
  inputWidth?: string
  inputBackground?: string
  inputHeight?: string
  disabled?: boolean
  hideMonthAndYearPicker?: boolean
  excludeDates?: Date[]
  onInputClick(): void
  onClickOutside(event: React.MouseEvent<HTMLDivElement>): void
  onChange(date: Date | [Date, Date] | null): void
  onChangeRaw?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  returnOnValidation?: (valid: boolean) => void
  // range
  startDate?: Date | null | undefined
  endDate?: Date | null | undefined
  selectsRange?: boolean
  showInput?: boolean
  inputDate?: string
  maxDate?: Date | null | undefined
  minDate?: Date | null | undefined
  // single
  selected?: Date | null
  popperPlacement?: PopperPlacement
  validation?: boolean
  strictParsing?: boolean
}

const DatepickerComponent = (props: Props): JSX.Element => {
  const {
    open,
    className,
    startDate,
    endDate,
    selectsRange,
    inputDate,
    inputColor,
    customInput,
    selected,
    inputBorder,
    inputBorderSmall,
    inputMargin,
    inputWidth,
    placeholder,
    disabled,
    inputBackground,
    inputHeight,
    popperPlacement,
    dateFormat,
    showInput,
    hideMonthAndYearPicker,
    maxDate,
    minDate,
    excludeDates,
    validation,
    onInputClick,
    onClickOutside,
    onChange,
    onBlur,
    onChangeRaw,
    returnOnValidation,
    strictParsing
  } = props

  const [inputValue, setInputValue] = useState(false)
  const [validationMsg, setValidationMsg] = useState('')

  useEffect(() => {
    if (inputBackground) {
      const elements = document.getElementsByClassName(inputBackground) as HTMLCollectionOf<HTMLElement>
      for (let i = 0; i < elements.length; i++) {
        elements[i].style.background = elements[i].className.split(' ').filter(name => name === inputBackground)[0]
      }
    }
  }, [inputBackground])

  useEffect(() => {
    if (inputWidth) {
      const elements = document.getElementsByClassName(inputWidth) as HTMLCollectionOf<HTMLElement>
      for (let i = 0; i < elements.length; i++) {
        elements[i].style.width = elements[i].className.split(' ').filter(name => name === inputWidth)[0]
      }
    }
  }, [inputWidth])

  useEffect(() => {
    if (inputHeight) {
      const elements = document.getElementsByClassName(inputHeight) as HTMLCollectionOf<HTMLElement>
      for (let i = 0; i < elements.length; i++) {
        elements[i].style.height = elements[i].className.split(' ').filter(name => name === inputHeight)[0]
      }
    }
  }, [inputHeight])

  const dayClassName = (date: Date) => {
    let _className = 'remove-highlight'
    if (moment(date).format('DD.MM.YYYY') === moment(new Date()).format('DD.MM.YYYY')) {
      _className = 'remove-highlight'
    }
    if (selected && moment(selected).format('DD.MM.YYYY') === moment(date).format('DD.MM.YYYY')) {
      _className = 'add-highlight'
    }
    if (startDate && endDate) {
      const _datesBetween = getDatesBetween(new Date(startDate), new Date(endDate))
      if (_datesBetween.includes(moment(date).format('YYYY-MM-DD'))) {
        _className = 'add-highlight'
      } else {
        _className = 'remove-highlight'
      }
    }
    return _className
  }

  const handleOnInputClick = () => {
    if (selectsRange) {
      setInputValue(true)
    }
    onInputClick()
  }

  const handleOnClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    setInputValue(false)
    onClickOutside(e)
  }

  const handleInputValidation = (e: React.FocusEvent | React.ChangeEvent) => {
    if (selectsRange) {
      setInputValue(true)
      const _value = e.target as HTMLInputElement
      let validationMessage = ''
      if (_value.value && inputDatesTypeRegex.regexPattern.test(_value.value)) {
        const _start = moment(_value.value.split('-')[0], 'DD.MM.YYYY').toDate()
        const _end = moment(_value.value.split('-')[1], 'DD.MM.YYYY').toDate()
        if (moment('01.01.2000', 'DD.MM.YYYY').isAfter(_start)) {
          validationMessage = 'Minimum start date is 01.01.2000'
        } else if (moment('01.01.2000', 'DD.MM.YYYY').isBefore(_start)) {
          validationMessage = ''
        }
        if (moment(_start).isAfter(_end)) {
          validationMessage = `${validationMessage !== '' ? validationMessage + ' and ' : ''}The start date cannot be greater than the end date`
        } else if (moment(_start).isBefore(_end)) {
          setValidationMsg('')
        }
        setValidationMsg(validationMessage)
        onChange([_start, _end])
        setInputValue(false)
      } else {
        const _start = _value.value.split(' - ')[0]
        const _end = _value.value.split(' - ')[1]
        if (_value.value && (
          (inputDateTypeRegex.regexPattern.test(_start) && !moment(_start, 'DD.MM.YYYY', true).isValid()) ||
          (inputDateTypeRegex.regexPattern.test(_end) && !moment(_value.value.split('-')[1], 'DD.MM.YYYY', true).isValid())
        )) {
          setValidationMsg('Wrong date format please use this format "DD.MM.YYYY"')
        } else {
          setValidationMsg('')
        }
      }
    }
  }

  const renderInputElement = () => {
    return (
      <p onFocus={() => setInputValue(true)}>
        <input
          style={{ padding: 0, border: 'none' }}
          onBlur={(e) => handleInputValidation(e)}
          onChange={(e) => handleInputValidation(e)}
          defaultValue={inputDate}
        />
      </p>
    )
  }

  const defaultOnChangeRaw = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (validation) {
      const _date = e.target.value
      if (_date) {
        if (!inputDateTypeFullYearRegex.regexPattern.test(_date)) {
          setValidationMsg('Wrong date format, please use this format "DD.MM.YYYY"')
          if (returnOnValidation) {
            returnOnValidation(false)
          }
        } else if (!moment(moment(_date, 'DD.MM.YYYY'), 'DD.MM.YYYY', true).isValid()) {
          setValidationMsg('Entered day or/and month does not exist')
          if (returnOnValidation) {
            returnOnValidation(false)
          }
        }
        else {
          setValidationMsg('')
          if (returnOnValidation) {
            returnOnValidation(true)
          }
        }
      } else {
        setValidationMsg('')
        if (returnOnValidation) {
          returnOnValidation(true)
        }
      }
    }
  }

  return (
    <Form.Field>
      <Popup
        className='popup warning'
        style={{ backgroundColor: 'red', borderColor: 'red' }}
        content={validationMsg}
        open={validationMsg !== ''}
        position='bottom center'
        trigger={
          <DatePicker
            className={`${className ? className : ''}
                ${disabled ? 'disable-datepicker' : ''}
                ${inputBorder ? 'add-border' : ''}
                ${inputBorderSmall ? 'add-border-small-input' : ''}
                ${open ? 'add-border-color' : ''}
                ${inputWidth ? inputWidth : ''}
                ${inputColor ? inputColor : ''}
                ${inputBackground ? inputBackground : ''}
                ${inputHeight ? inputHeight : ''}`}
            popperClassName={`${hideMonthAndYearPicker ? '' : 'show-month-year-picker'}`}
            wrapperClassName={inputMargin ? 'add-margin' : ''}
            open={open}
            onClickOutside={(e) => handleOnClickOutside(e)}
            onInputClick={handleOnInputClick}
            onSelect={() => setInputValue(false)}
            onChange={onChange}
            strictParsing={strictParsing}
            startDate={startDate}
            minDate={minDate ?? new Date('01.01.2000')}
            maxDate={maxDate}
            endDate={endDate}
            shouldCloseOnSelect
            selectsRange={selectsRange}
            customInput={!inputValue || !showInput ? customInput : selectsRange ? renderInputElement() : undefined}
            selected={selected}
            dayClassName={(date) => dayClassName(date)}
            placeholderText={placeholder}
            locale={'en-gb'}
            dateFormat={dateFormat}
            popperPlacement={popperPlacement}
            showYearDropdown={!hideMonthAndYearPicker}
            showMonthDropdown={!hideMonthAndYearPicker}
            dropdownMode='select'
            excludeDates={excludeDates}
            onBlur={onBlur}
            onChangeRaw={onChangeRaw ?? defaultOnChangeRaw}
          />
        }
      />
    </Form.Field>
  )
}

export default DatepickerComponent
